// Utility functions for importing and exporting .geniaweb files

/**
 * Exports the current flow state to a .geniaweb file
 * @param {Object} flowData - The complete flow data including nodes, edges, variables, avatars, and rects
 * @param {String} filename - The desired filename (without extension)
 */
export const exportToGeniaWeb = (flowData, filename = 'fluxograma') => {
  try {
    // Create the complete data structure with proper validation
    const exportData = {
      version: '1.0.0',
      createdAt: new Date().toISOString(),
      appName: 'GeniaWeb',
      flowData: {
        nodes: Array.isArray(flowData.nodes) ? flowData.nodes : [],
        edges: Array.isArray(flowData.edges) ? flowData.edges : [],
        variables: Array.isArray(flowData.variables) ? flowData.variables : [],
        avatars: Array.isArray(flowData.avatars) ? flowData.avatars : [],
        rects: Array.isArray(flowData.rects) ? flowData.rects : []
      }
    };

    // Convert to JSON string
    const jsonString = JSON.stringify(exportData, null, 2);
    
    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.geniaweb`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
    
    return { success: true, message: 'Arquivo exportado com sucesso!' };
  } catch (error) {
    console.error('Erro ao exportar arquivo:', error);
    return { success: false, message: 'Erro ao exportar arquivo: ' + error.message };
  }
};

/**
 * Imports a .geniaweb file and returns the flow data
 * @param {File} file - The file object from input
 * @returns {Promise} Promise that resolves with the imported data
 */
export const importFromGeniaWeb = (file) => {
  return new Promise((resolve, reject) => {
    try {
      // Validate file extension
      if (!file.name.endsWith('.geniaweb')) {
        reject(new Error('Formato de arquivo inválido. Use apenas arquivos .geniaweb'));
        return;
      }

      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          
          // Validate data structure
          if (!jsonData.flowData) {
            reject(new Error('Arquivo inválido: estrutura de dados não encontrada'));
            return;
          }

          // Validate required fields
          const requiredFields = ['nodes', 'edges'];
          for (const field of requiredFields) {
            if (!Array.isArray(jsonData.flowData[field])) {
              reject(new Error(`Arquivo inválido: campo '${field}' não encontrado ou inválido`));
              return;
            }
          }

          resolve({
            success: true,
            data: jsonData.flowData,
            metadata: {
              version: jsonData.version,
              createdAt: jsonData.createdAt,
              appName: jsonData.appName
            },
            message: 'Arquivo importado com sucesso!'
          });
        } catch (parseError) {
          reject(new Error('Erro ao processar arquivo: arquivo pode estar corrompido'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Erro ao ler arquivo'));
      };

      reader.readAsText(file);
    } catch (error) {
      reject(new Error('Erro inesperado: ' + error.message));
    }
  });
};

/**
 * Validates if the imported data is compatible with the current application
 * @param {Object} flowData - The imported flow data
 * @returns {Object} Validation result
 */
export const validateImportedData = (flowData) => {
  const errors = [];
  const warnings = [];

  // Check nodes structure
  if (flowData.nodes) {
    if (!Array.isArray(flowData.nodes)) {
      errors.push("Nodes deve ser um array");
    } else {
      flowData.nodes.forEach((node, index) => {
        if (!node.id) {
          errors.push(`Nó ${index + 1}: ID não encontrado`);
        }
        if (!node.type) {
          errors.push(`Nó ${index + 1}: tipo não encontrado`);
        }
        if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
          warnings.push(`Nó ${index + 1}: posição inválida`);
        }
      });
    }
  }

  // Check edges structure
  if (flowData.edges) {
    if (!Array.isArray(flowData.edges)) {
      errors.push("Edges deve ser um array");
    } else {
      flowData.edges.forEach((edge, index) => {
        if (!edge.id) {
          errors.push(`Conexão ${index + 1}: ID não encontrado`);
        }
        if (!edge.source || !edge.target) {
          errors.push(`Conexão ${index + 1}: origem ou destino não encontrado`);
        }
      });
    }
  }

  // Check variables structure - only warn if data exists but is invalid
  if (flowData.variables && !Array.isArray(flowData.variables)) {
    warnings.push("Dados de variáveis inválidos - serão ignorados");
  }

  // Check avatars structure - only warn if data exists but is invalid
  if (flowData.avatars && !Array.isArray(flowData.avatars)) {
    warnings.push("Dados de avatares inválidos - serão ignorados");
  }

  // Check rects structure (internal, don't warn user)
  // Rects are handled internally and don't need user warning

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Creates a backup of the current state before importing
 * @param {Object} currentState - Current application state
 * @returns {Object} Backup data
 */
export const createBackup = (currentState) => {
  return {
    timestamp: new Date().toISOString(),
    data: JSON.parse(JSON.stringify(currentState)) // Deep clone
  };
};
